import Block from "@DSG/Icons/Block";
import Pickup from "@DSG/Icons/Pickup";
import Truck from "@DSG/MobiusOverrides/Icons/Truck";
import colorPallete, { bodyFonts, elevation, headlineFonts, themeFonts, themeFontWeights } from "@DSG/MobiusOverrides/styles/variables";
import { labelFonts } from "@DSG/MobiusOverrides/styles/variables";
import { setCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityDto,
    AvailabilityMessageType,
    InventoryWarehousesDto,
    ProductInventoryDto,
    ProductSettingsModel,
    WarehouseDto,
} from "@insite/client-framework/Types/ApiModels";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import Check from "@insite/mobius/Icons/Check";
import X from "@insite/mobius/Icons/X";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import safeColor from "@insite/mobius/utilities/safeColor";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";


interface OwnProps {
    productId: string;
    availability?: AvailabilityDto;
    unitOfMeasure: string;
    trackInventory: boolean;
    isProductDetailsPage?: boolean;
    failedToLoadInventory?: boolean;
    extendedStyles?: ProductAvailabilityStyles;
    inventory?: ProductInventoryDto;
    isDiscontinued?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    showInventory: state.context.showInventory,
    session: state.context.session,
    pickUpWarehouse: state.context.session.pickUpWarehouse || state.pages.accountSettings.editingAccount?.defaultWarehouse,

});

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductAvailabilityStyles {
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    availabilityByWarehouseLinkGridItem?: GridItemProps;
    inventoryMessage?: InjectableCss;
    messageText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    errorWrapper?: InjectableCss;
    errorText?: TypographyPresentationProps;
    availabilityByWarehouseLink?: LinkPresentationProps;
    availabilityByWarehouseModal?: ModalPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    availabilityByWarehouseTable?: DataTableProps;
    availabilityByWarehouseTableHead?: DataTableHeadProps;
    availabilityByWarehouseTableHeader?: DataTableHeaderProps;
    availabilityByWarehouseTableBody?: DataTableBodyProps;
    availabilityByWarehouseTableRow?: DataTableRowProps;
    availabilityByWarehouseTableCell?: DataTableCellBaseProps;
    availabilityByWarehouseGridItem?: GridItemProps;
    availabilityByWarehousePickUpAndDeliverGridItem?: GridItemProps;
    inventoryAvailableHeading?: InjectableCss;
    inventoryNotAvailableValue?: InjectableCss;
    inventoryAvailableValue?: InjectableCss;
    inventoryWrapper?: InjectableCss;
    inventoryInnerWrapper?: InjectableCss;
    inventoryLinkWrapper?: InjectableCss;
    availableForPurchaseWrapper?: InjectableCss;
}

export const productAvailabilityStyles: ProductAvailabilityStyles = {
    availableForPurchaseWrapper: {
        css: css`
            &.availableForPurchase {
                font: ${bodyFonts.body2RegularSm.fontWeight} ${bodyFonts.body2RegularSm.fontSize}/${bodyFonts.body2RegularSm.lineHeight} ${bodyFonts.body2RegularSm.fontFamily};
                color: ${colorPallete.semanticGreen500};
                margin-bottom: 16px;
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                        font-size: ${bodyFonts.body2RegularLg.fontSize};
                        line-height: ${bodyFonts.body2RegularLg.lineHeight};
                    `,
                    ],
                    "min",
                )}
            }
        `
    },
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin-bottom: 16px;
            
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                        margin-bottom: 24px;
                    `,
                    ],
                    "min",
                )}
        `,
    },
    messageGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `
    },
    availabilityByWarehouseLinkGridItem: {
        width: 12,
        css: css`
            padding-bottom: 0;
            [data-test-selector="availabilityByWarehouseLink"] {
                margin-top: 16px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                        margin-top: 24px;
                    `,
                    ],
                    "min",
                )}

        `
    },
    availabilityByWarehouseModal: {
        size: 400,
        cssOverrides: {
            modalContent: css`
                overflow-y: auto;
                padding: 0;
                margin-top: 58px;
                border-radius: 0 0 8px 8px;
                &::-webkit-scrollbar-track{
                    border-radius: 40px;
                    background-color: transparent;
                }
                &::-webkit-scrollbar {
                    width: 4px;
                    background-color: transparent;
                }
                &::-webkit-scrollbar-thumb {
                    border-radius: 40px;
                    background-color: ${colorPallete.grey800};	
                }
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            &[class*="ModalContent"] {
                                padding: 0;
                            }
                        `,
                    ],
                    "min",
                )}
            `,
            modalTitle: css`
                padding: 16px;
                position: sticky;
                top: 0;
                background: ${colorPallete.white};
                border-bottom: 1px solid ${colorPallete.grey500};
                border-radius: 8px 8px 0 0;
                [data-test-selector="modalCloseButton"] {
                    min-height: initial;
                    padding: 0;
                    background: transparent;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            &[class*="ModalTitle"] {
                                padding: 16px;
                            }
                        `,
                    ],
                    "min",
                )}
            `,
            headlineTypography: css`
                margin-bottom: 0;
                color: ${colorPallete.grey800};
                font: ${headlineFonts.headline4SemiBoldLg.fontWeight} ${headlineFonts.headline4SemiBoldLg.fontSize}/30px ${headlineFonts.headline4SemiBoldLg.fontFamily};
            `,
            modalBody: css`
                > div {
                    display: flex;
                    flex-direction: column;
                    overflow-y: auto;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                max-width: 390px;
                                overflow: initial;
                            `,
                            null,
                            css`
                                border-radius: 8px;
                            `
                        ],
                        "min",
                    )}
            `
        },
    },
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: 0 auto;
        `,
    },
    realTimeText: {
        weight: "bold",
        css: css`
            display: inline-block;
            text-align: left;
            min-width: 25px;

            &::after {
                overflow: hidden;
                display: inline-block;
                vertical-align: bottom;
                animation: ellipsis steps(4, end) 900ms infinite 1s;
                content: "\\2026";
                width: 0;
            }

            @keyframes ellipsis {
                to {
                    width: 1.25em;
                }
            }
        `,
    },
    errorText: {
        css: css`
            color: ${colorPallete.grey600DisabledText};
            margin: 8px 0;
            font: ${bodyFonts.body2RegularLg.fontWeight} ${bodyFonts.body2RegularLg.fontSize}/${bodyFonts.body2RegularLg.lineHeight} ${bodyFonts.body2RegularLg.fontFamily};
        `
    },
    availabilityByWarehouseTable: {
        css: css`
            thead {
                position: absolute;
                width: 100%;
                top: 63px;
                box-shadow: ${elevation.dp08};
                &, th {
                    background: ${colorPallete.white};
                    border: none;
                }
                tr {
                    display: flex;
                    position: relative;
                    &:after {
                        content: "";
                        border-bottom: 1px solid ${colorPallete.grey500};
                        position: absolute;
                        width: 100%;
                        left: 0;
                        bottom:0;
                    }
                }
            }
            tbody tr {
                border: none;
            }
            th {
                border-top: 1px solid ${colorPallete.grey500};
                border-bottom: 1px solid ${colorPallete.grey500};
                &, span {
                    font: ${bodyFonts.body1SemiBoldSm.fontWeight} ${bodyFonts.body1SemiBoldSm.fontSize}/${bodyFonts.body1SemiBoldSm.lineHeight} ${bodyFonts.body1SemiBoldSm.fontFamily};
                }
            }
            td {
                &, span {
                    font: ${bodyFonts.body1RegularSm.fontWeight} ${bodyFonts.body1RegularSm.fontSize}/${bodyFonts.body1RegularSm.lineHeight} ${bodyFonts.body1RegularSm.fontFamily};
                }
            }
            th, td {
                height: auto;
                width: 50%;
                padding: 16px 40px;
                min-height: 56px;
                &:nth-child(even) {
                    text-align: right;
                }
            }
            td {
                min-height: 58px;
                white-space: normal;
            }
        `
    },
    availabilityByWarehouseTableRow: {
        css: css`
            &:nth-child(even) {
                background: ${colorPallete.white};
            }
            &:nth-child(odd) {
                background: ${colorPallete.grey100};
            }
        `
    },
    inventoryAvailableValue: {
        css: css`
            color: ${colorPallete.semanticGreen500};
            > span,
            > span > span {
                display: block;
            }
            &.notAvailable {
                color: ${colorPallete.semanticError};
                > span,
                > span > span {
                    display: block;
                }
            }
        `
    },
    inventoryNotAvailableValue: {
        css: css`
            color: ${colorPallete.semanticError};
            > span {
                display: block;
            }
        `
    },
    availabilityByWarehousePickUpAndDeliverGridItem: {
        width: 12,
        css: css`
        ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                    flex-direction: column;
                `,
                    ],
                    "max",
                )}
        `
    },
    inventoryWrapper: {
        css: css`
            &:first-child {
                padding-right: 40px;
            }
            &:nth-child(2) {
                padding-right: 4px;
            }
            > div {
                display: flex;
                column-gap: 4px;
                max-width: 215px;
                flex-direction: row;
                font: ${bodyFonts.body2RegularLg.fontWeight} ${bodyFonts.body2RegularLg.fontSize}/${bodyFonts.body2RegularLg.lineHeight}  ${bodyFonts.body2RegularLg.fontFamily};
                color: ${colorPallete.grey800};
                strong {
                    font-weight: ${bodyFonts.body2SemiBoldLg.fontWeight};
                }
               
                svg {
                    width: 24px;
                    height: 24px;
                    min-width: 24px;
                }
            }
        `
    },
    availabilityByWarehouseLink: {
        css: css`
            text-decoration: underline;
            font: ${labelFonts.label1Regular.fontWeight} ${labelFonts.label1Regular.fontSize}/${labelFonts.label1Regular.lineHeight}  ${labelFonts.label1Regular.fontFamily};
            display: block;
            padding-left: 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                        margin-top: 8px;
                    `,
                    ],
                    "max",
                )}
        `
    }
};

const InventoryMessage = styled.div<{ color: string }>`
   
`;

const getAvailabilityColor = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return "common";
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return "danger";
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return "warning";
    }

    return "success";
};

const getAvailabilityIcon = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return undefined;
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return X;
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return AlertTriangle;
    }

    return Check;
};

const showLink = (
    availability: AvailabilityDto | undefined,
    trackInventory: boolean,
    productSettings: ProductSettingsModel,
    isProductDetailsPage?: boolean,
    totalQtyOnHand?: number,
): boolean => {

    if (
        !availability ||
        availability.messageType === AvailabilityMessageType.NoMessage ||
        !trackInventory ||
        !productSettings.showInventoryAvailability ||
        !productSettings.displayInventoryPerWarehouse
    ) {
        return false;
    }

    if (!productSettings.displayInventoryPerWarehouseOnlyOnProductDetail) {
        return true;
    }
    if(totalQtyOnHand && totalQtyOnHand > 0)
    {
        return true;
    }

    return false;

};

const ProductAvailability = ({
    productId,
    availability,
    unitOfMeasure,
    trackInventory,
    productSettings,
    isProductDetailsPage,
    inventory,
    getRealTimeWarehouseInventory,
    failedToLoadInventory,
    showInventory,
    extendedStyles,
    session,
    isDiscontinued,
    pickUpWarehouse,
}: Props) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [warehouses, setWarehouses] = useState<WarehouseDto[] | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useState("");
    const [pickupMessage, setPickupMessage] = useState("");
    const [deliveryMessage, setDeliveryMessage] = useState("");
    const [selectedWarehouseName, setSelectedWarehouseName] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isInventoryLoaded, setIsInventoryLoaded] = useState(false);
    const [isPickupTiming, setIsPickupTiming] = useState(false);
    const [isDeliveryAvailableTiming, setIsDeliveryAvailableTiming] = useState(false);
    const [totalQtyOnHand, SetTotalQtyOnHand] = useState(0);
    const [seoInventoryMessage, setSeoInventoryMessage] = useState("");
    // const [inventoryWarehouses, setInventoryWarehouses] = useState<WarehouseDto[] | undefined>(undefined);

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };
    const viewAvailabilityByWarehouseClickHandler = () => {
        setIsLoading(true);
        setModalIsOpen(true);
        getRealTimeWarehouseInventory({
            productId,
            unitOfMeasure,
            expand: ["warehouses"],
            onComplete: result => {
                setWarehouses(result.warehouses);
                setErrorMessage(result.errorMessage);
                setIsLoading(false);
            },
        });
    };


    const color = getAvailabilityColor(productSettings, availability);
    const iconSrc = getAvailabilityIcon(productSettings, availability);
    let notAvailableClass = "";
    const [styles] = useState(() => mergeToNew(productAvailabilityStyles, extendedStyles));

    if (!showInventory) {
        return null;
    }

    if (failedToLoadInventory) {
        return (
            <StyledWrapper {...styles.errorWrapper} className="failedToLoad">
               <Icon src={Block} /> <Typography {...styles.errorText}>{siteMessage("RealTimeInventory_InventoryLoadFailed")}</Typography>
            </StyledWrapper>
        );
    }
    
    if (session.isAuthenticated && inventory)
    {
    if(!isInventoryLoaded)
    {
        if(inventory.inventoryWarehousesDtos)
        {
           const inventoryWarehouses = inventory.inventoryWarehousesDtos?.[0]?.warehouseDtos;


        if(inventoryWarehouses && inventoryWarehouses.length > 0)
        {
            let warehouse : WarehouseDto | undefined;
            let totalQtyOnHand = 0;

            for (let i = 0; i < inventoryWarehouses.length; i++) {
                // Push each item's JSX representation into the items array
                totalQtyOnHand += inventoryWarehouses[i].qty;
              }
              SetTotalQtyOnHand(totalQtyOnHand);

            if(pickUpWarehouse)
            {
                setSelectedWarehouseName(pickUpWarehouse.name);
                warehouse = inventoryWarehouses?.filter(x => x.description === pickUpWarehouse.name)?.[0] || undefined;
            }
            if(warehouse && warehouse.qty > 0)// Selected branch
            {       
                setPickupMessage(translate(siteMessage("Inventory_AvailableStock").toString(), warehouse.qty.toString()));
                setIsPickupTiming(false);
                setSeoInventoryMessage("InStock");
            }
            else
            {
                // const topWarehouse : WarehouseDto | undefined = inventoryWarehouses?.[0];
             

                if(pickUpWarehouse && totalQtyOnHand > 0) // ISE In stock elsewhere
                {
                    setPickupMessage(pickUpWarehouse?.name);
                    // setPickupMessage(siteMessage("Inventory_ShipsToPickupTimingVaries").toString());
                    setIsPickupTiming(true);
                    setSeoInventoryMessage("InStock");
                }
                if(isDiscontinued && totalQtyOnHand > 0)  // Discontinued but remaining inventory available elsewhere.
                {
                    setPickupMessage(siteMessage("Inventory_NotAvailableToPickup").toString());
                    notAvailableClass = "notAvailable";
                    setIsPickupTiming(false);
                    setSeoInventoryMessage("InStock");
                }

            }
                // Delivery
                if(warehouse && warehouse.qty > 0 && isDiscontinued)
                {
                    setDeliveryMessage(translate(siteMessage("Inventory_AvailableStock").toString(), totalQtyOnHand.toString()));
                    setSeoInventoryMessage("InStock");
                }
                else if(totalQtyOnHand > 0 && isDiscontinued)
                {
                    setDeliveryMessage(siteMessage("Inventory_Available").toString());
                    setSeoInventoryMessage("InStock");
                }
                else if(totalQtyOnHand > 0){
                    setDeliveryMessage(translate(siteMessage("Inventory_AvailableStock").toString(), totalQtyOnHand.toString()));
                    setSeoInventoryMessage("InStock");
                }
                else if(totalQtyOnHand <= 0)
                {
                    setIsDeliveryAvailableTiming(true);
                    setSeoInventoryMessage("OutOfStock");
                // setDeliveryMessage(siteMessage("Inventory_AvailableDeliveryTimeVaries").toString());
                }
        }
        else
        if(pickUpWarehouse)
            {
                // setPickupMessage("<span>Ships to " + pickUpWarehouse.name + "</span>.<span>Pickup timing varies</span>");
                // setPickupMessage(siteMessage("Inventory_ShipsToPickupTimingVaries").toString());
                setIsPickupTiming(true);
                setPickupMessage(pickUpWarehouse.name);
                setIsDeliveryAvailableTiming(true);
                setSeoInventoryMessage("OutOfStock");
            }
        
    }
    
    setIsInventoryLoaded(true);
    }
        
   }

    if (!session.isAuthenticated) {
        return (
            <StyledWrapper {...styles.availableForPurchaseWrapper} className="availableForPurchase">
                {/* <Typography {...styles.errorText}>{siteMessage("RealTimeInventory_InventoryLoadFailed")}</Typography> */}
                {siteMessage("Inventory_AvailableForPurchase")}
            </StyledWrapper>
        );
    }

    let inventoryTextComponent = <Typography {...styles.realTimeText} />;
    if (pickupMessage && pickupMessage !== "") {
        inventoryTextComponent = (
            <InventoryMessage className="PickUpAtAvailable" color={color} {...styles.inventoryMessage}>
                <Pickup />
                <StyledWrapper {...styles.inventoryInnerWrapper} data-test-selector="availabilityMessage">
                    <Typography as="p" {...styles.inventoryAvailableHeading}>
                        <Typography as="strong">
                            {translate("Pick Up ")}
                        </Typography>
                        <Typography>
                            {translate("at ")}
                        </Typography>
                        <Typography className="pickUpWarehouse">
                            {pickUpWarehouse?.name}
                        </Typography>
                    </Typography>
                    <Typography as="p" {...styles.inventoryAvailableValue} className={notAvailableClass}>
                        <Typography>
                            {isPickupTiming ? siteMessage("Inventory_ShipsToPickupTimingVaries", pickupMessage) : pickupMessage}
                        </Typography>
                    </Typography>
                </StyledWrapper>
            </InventoryMessage>
        );
    } else if (availability) {
        inventoryTextComponent = <></>;
    }
    let warehouseTextComponent = <Typography {...styles.realTimeText} />;
    if ((deliveryMessage && deliveryMessage !== "") || isDeliveryAvailableTiming) {
        warehouseTextComponent = (
            <InventoryMessage className="DeliveryAvailable" color={color} {...styles.inventoryMessage}>
                <Truck />
                <StyledWrapper {...styles.inventoryInnerWrapper} data-test-selector="availabilityMessage">
                    <Typography as="p" {...styles.inventoryAvailableHeading}>
                        <Typography as="strong">
                            {translate("Delivery")}
                        </Typography>
                    </Typography>
                    <Typography as="p" {...styles.inventoryAvailableValue}>
                        <Typography>
                            {isDeliveryAvailableTiming ? siteMessage("Inventory_AvailableDeliveryTimeVaries") : deliveryMessage}
                        </Typography>
                    </Typography>
                </StyledWrapper>
            </InventoryMessage>
        );
    } else {
        warehouseTextComponent = <></>;
    }
    setCookie("ProductSchemaInventoryMessage",seoInventoryMessage);
    return (
        <GridContainer {...styles.container} className="productAvailability">
            <GridItem {...styles.availabilityByWarehousePickUpAndDeliverGridItem}>
                <StyledWrapper className="inventoryWrapper"  {...styles.inventoryWrapper}>
                    {inventoryTextComponent}
                </StyledWrapper>
                <StyledWrapper className="inventoryWrapper" {...styles.inventoryWrapper}>
                    {warehouseTextComponent}
                </StyledWrapper>
                <StyledWrapper className="inventoryLinkWrapper" {...styles.inventoryLinkWrapper}>
                        {showLink(availability, trackInventory, productSettings, isProductDetailsPage,totalQtyOnHand) && (
                        <>
                            <Link
                                {...styles.availabilityByWarehouseLink}
                                onClick={viewAvailabilityByWarehouseClickHandler}
                                data-test-selector="availabilityByWarehouseLink"
                            >
                                {translate("View inventory details")}
                            </Link>
                            <Modal
                                {...styles.availabilityByWarehouseModal}
                                headline={translate("All Inventory")}
                                isOpen={modalIsOpen}
                                handleClose={modalCloseHandler}
                            >
                                {isLoading && (
                                    <StyledWrapper {...styles.centeringWrapper}>
                                        <LoadingSpinner {...styles.spinner} />
                                    </StyledWrapper>
                                )}
                                {!isLoading && (
                                    <>
                                        {errorMessage ? (
                                            <div>{errorMessage}</div>
                                        ) : (
                                            <DataTable
                                                {...styles.availabilityByWarehouseTable}
                                                data-test-selector="availabilityByWarehouseModal"
                                            >
                                                <DataTableHead {...styles.availabilityByWarehouseTableHead}>
                                                    <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                        {translate("Warehouse")}
                                                    </DataTableHeader>
                                                    <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                        {translate("Available Qty.")}
                                                    </DataTableHeader>
                                                </DataTableHead>
                                                <DataTableBody {...styles.availabilityByWarehouseTableBody}>
                                                    {warehouses?.map(warehouse => (
                                                        <DataTableRow
                                                            {...styles.availabilityByWarehouseTableRow}
                                                            key={warehouse.name}
                                                        >
                                                            <DataTableCell
                                                                {...styles.availabilityByWarehouseTableCell}
                                                                data-test-selector="warehouseAvailabilityModal_Description"
                                                            >
                                                                {warehouse.description || warehouse.name}
                                                            </DataTableCell>
                                                            <DataTableCell
                                                                {...styles.availabilityByWarehouseTableCell}
                                                                data-test-selector="warehouseAvailabilityModal_Quantity"
                                                            >
                                                                {warehouse.qty}
                                                            </DataTableCell>
                                                        </DataTableRow>
                                                    ))}
                                                </DataTableBody>
                                            </DataTable>
                                        )}
                                    </>
                                )}
                            </Modal>
                        </>
                    )}
                </StyledWrapper>
            </GridItem>

        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductAvailability);
